import React, { useState, useEffect, useRef } from 'react';
import { Star, Filter, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ReviewCard } from './ReviewCard';
import { useReviews, Review, UserReviewAggregate } from '@/hooks/useReviews';

interface ReviewsSectionProps {
  userId: string;
  userType: 'client' | 'vendor';
  showTitle?: boolean;
  // Optional: pass data from parent to avoid duplicate fetching
  providedReviews?: Review[];
  providedAggregate?: UserReviewAggregate | null;
  providedLoading?: boolean;
}

const StarDisplay: React.FC<{ rating: number; count?: number }> = ({ rating, count }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= Math.round(rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground'
            }`}
          />
        ))}
      </div>
      <span className="font-medium">{rating.toFixed(1)}</span>
      {count !== undefined && (
        <span className="text-sm text-muted-foreground">({count} reviews)</span>
      )}
    </div>
  );
};

export const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  userId,
  userType,
  showTitle = true,
  providedReviews,
  providedAggregate,
  providedLoading
}) => {
  // Use provided data if available (presentational mode), otherwise fetch ourselves
  const isProvidedMode = providedReviews !== undefined;
  
  const [localReviews, setLocalReviews] = useState<Review[]>([]);
  const [localAggregate, setLocalAggregate] = useState<UserReviewAggregate | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [displayCount, setDisplayCount] = useState(5);
  
  // Track which userId we already fetched to prevent re-fetching
  const fetchedUserIdRef = useRef<string | null>(null);
  
  const { getUserReviews, getUserReviewAggregate } = useReviews();

  // Only fetch if not in provided mode and we haven't fetched this userId yet
  useEffect(() => {
    if (isProvidedMode) return; // Skip if data is provided from parent
    if (fetchedUserIdRef.current === userId) return; // Already fetched for this user
    
    let isCancelled = false;
    const loadReviews = async () => {
      setLocalLoading(true);
      fetchedUserIdRef.current = userId; // Mark as fetching
      
      try {
        const [reviewsData, aggregateData] = await Promise.all([
          getUserReviews(userId),
          getUserReviewAggregate(userId)
        ]);
        
        if (!isCancelled) {
          setLocalReviews(reviewsData);
          setLocalAggregate(aggregateData);
        }
      } finally {
        if (!isCancelled) {
          setLocalLoading(false);
        }
      }
    };

    loadReviews();
    return () => { isCancelled = true; };
  }, [userId, isProvidedMode, getUserReviews, getUserReviewAggregate]);

  // Use provided or local data
  const reviews = isProvidedMode ? (providedReviews || []) : localReviews;
  const aggregate = isProvidedMode ? (providedAggregate ?? null) : localAggregate;
  const loading = isProvidedMode ? (providedLoading ?? false) : localLoading;

  useEffect(() => {
    let filtered = reviews;

    if (filter !== 'all') {
      const rating = parseInt(filter);
      filtered = reviews.filter(review => review.overall_rating === rating);
    }

    setFilteredReviews(filtered);
  }, [reviews, filter]);

  const getTitle = () => {
    if (!showTitle) return null;
    return userType === 'vendor' ? 'Client Reviews' : 'Vendor Reviews';
  };

  const getVisibilityNote = () => {
    return userType === 'client' 
      ? 'Reviews visible to vendors only'
      : 'Reviews visible to all clients';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading reviews...</div>
        </CardContent>
      </Card>
    );
  }

  if (!aggregate || aggregate.total_reviews === 0) {
    return (
      <Card>
        <CardHeader>
          {showTitle && <CardTitle>{getTitle()}</CardTitle>}
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            No reviews yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        {showTitle && <CardTitle>{getTitle()}</CardTitle>}
        <div className="text-xs text-muted-foreground">{getVisibilityNote()}</div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Rating Summary */}
        <div className="space-y-4">
          <StarDisplay rating={aggregate.avg_overall_rating} count={aggregate.total_reviews} />
          
          {/* Detailed Rating Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {aggregate.avg_quality_rating && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Quality:</span>
                <StarDisplay rating={aggregate.avg_quality_rating} />
              </div>
            )}
            {aggregate.avg_timeliness_rating && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Timeliness:</span>
                <StarDisplay rating={aggregate.avg_timeliness_rating} />
              </div>
            )}
            {aggregate.avg_communication_rating && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Communication:</span>
                <StarDisplay rating={aggregate.avg_communication_rating} />
              </div>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        {reviews.length > 0 && (
          <div className="flex items-center gap-2 border-t pt-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reviews</SelectItem>
                <SelectItem value="5">5 Stars</SelectItem>
                <SelectItem value="4">4 Stars</SelectItem>
                <SelectItem value="3">3 Stars</SelectItem>
                <SelectItem value="2">2 Stars</SelectItem>
                <SelectItem value="1">1 Star</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          {filteredReviews.slice(0, displayCount).map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              showReviewerType={true}
            />
          ))}
        </div>

        {/* Load More Button */}
        {filteredReviews.length > displayCount && (
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setDisplayCount(prev => prev + 5)}
              className="flex items-center gap-2"
            >
              <ChevronDown className="h-4 w-4" />
              Load More Reviews
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};